import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { z } from "zod";
import { ChevronsUpDown, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

import { AccessKeysTable } from "@/components/access-keys/access-keys-table";
import { CreateAccessKeyCard } from "@/components/access-keys/create-access-key-card";
import { SecretDialog } from "@/components/access-keys/secret-dialog";
import type { IssuedKey } from "@/components/access-keys/secret-dialog";
import {
  accessKeyErrorMessage,
  useDeleteAccessKey,
  useTenantAccessKeys,
} from "@/hooks/use-access-keys";
import type { AccessKey } from "@/hooks/use-access-keys";
import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel } from "@/lib/cells/cells";

const searchSchema = z.object({
  tenantId: z.string().optional(),
});

// Harmony Internal (prod): never a key owner's tenant, as impersonation is
// never allowed into it
const BLOCKED_TENANT_IDS = new Set(["e3d883b2-e627-47b7-9bb9-3398a2ba8dd0"]);

export const Route = createFileRoute("/access-keys")({
  component: AccessKeysPage,
  validateSearch: zodValidator(searchSchema),
});

function AccessKeysPage() {
  const { tenantId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tenantOpen, setTenantOpen] = useState(false);
  // Page-level, so switching tenant mid-create cannot drop the one-time secret
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  const {
    data: tenants,
    isLoading: tenantsLoading,
    isError: tenantsFailed,
    failures: tenantFailures,
  } = useAllTenants();

  const sortedTenants = useMemo(
    () =>
      (tenants ?? [])
        .filter((tenant) => !BLOCKED_TENANT_IDS.has(tenant.id))
        .sort((a, b) => a.company.localeCompare(b.company)),
    [tenants],
  );
  const selectedTenant = sortedTenants.find((t) => t.id === tenantId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">API Keys</h1>
        <p className="text-muted-foreground">
          Issue platform API keys for a customer and see the keys their tenant
          has. A key is always owned by a person in the customer&apos;s tenant,
          never by a Harmony operator.
        </p>
      </div>
      <Separator />

      <div className="mx-auto max-w-6xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Tenant</CardTitle>
            <CardDescription>
              Pick the customer tenant to work in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Popover open={tenantOpen} onOpenChange={setTenantOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tenantOpen}
                  className="w-[320px] justify-between"
                >
                  {tenantsLoading
                    ? "Loading…"
                    : selectedTenant
                      ? tenantLabel(selectedTenant)
                      : "Select tenant"}
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0" align="start">
                <Command>
                  <CellTenantsUnavailable failures={tenantFailures} />
                  <CommandInput placeholder="Search tenants…" />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>No tenant found.</CommandEmpty>
                    <CommandGroup>
                      {sortedTenants.map((tenant) => (
                        <CommandItem
                          key={tenant.id}
                          value={`${tenantLabel(tenant)} ${tenant.id}`}
                          onSelect={() => {
                            void navigate({ search: { tenantId: tenant.id } });
                            setTenantOpen(false);
                          }}
                          className={cn(tenantId === tenant.id && "bg-accent")}
                        >
                          {tenantLabel(tenant)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {selectedTenant ? (
          <TenantAccessKeys
            key={selectedTenant.id}
            tenantId={selectedTenant.id}
            tenantName={selectedTenant.company}
            onIssued={setIssued}
          />
        ) : tenantId && tenantsLoading ? (
          <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading tenant…
          </div>
        ) : (
          <Empty className="py-16">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <KeyRound />
              </EmptyMedia>
              <EmptyTitle>
                {tenantsFailed
                  ? "Tenants could not be loaded"
                  : tenantId
                    ? "Tenant not found"
                    : "Select a tenant"}
              </EmptyTitle>
              <EmptyDescription>
                Keys are always issued and listed for one customer tenant.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>

      <SecretDialog
        issued={issued}
        onClose={() => {
          setIssued(null);
        }}
      />
    </div>
  );
}

interface TenantAccessKeysProps {
  tenantId: string;
  tenantName: string;
  onIssued: (issued: IssuedKey) => void;
}

/** One tenant's keys. Keyed by tenant, so no form state crosses tenants. */
function TenantAccessKeys({
  tenantId,
  tenantName,
  onIssued,
}: TenantAccessKeysProps) {
  const keys = useTenantAccessKeys(tenantId);
  const deleteKey = useDeleteAccessKey(tenantId);

  const shown = keys.data?.items ?? [];
  const total = keys.data?.total_count ?? 0;

  const handleDelete = async (key: AccessKey) => {
    try {
      await deleteKey.mutateAsync(key.access_key_id);
      toast.success(`Deleted "${key.name}"`);
    } catch (error: unknown) {
      toast.error(
        accessKeyErrorMessage(error, `Could not delete "${key.name}"`),
      );
    }
  };

  return (
    <>
      <CreateAccessKeyCard
        tenantId={tenantId}
        tenantName={tenantName}
        existingNames={shown.map((key) => key.name)}
        onIssued={onIssued}
      />
      <Card>
        <CardHeader>
          <CardTitle>Keys for {tenantName}</CardTitle>
          <CardDescription>
            {total > shown.length
              ? `Showing ${shown.length} of ${total} active keys.`
              : "Active platform and webhook keys of this tenant."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {keys.isError ? (
            <p className="text-destructive py-8 text-center text-sm">
              {accessKeyErrorMessage(keys.error, "Keys could not be loaded")}
            </p>
          ) : (
            <AccessKeysTable
              keys={shown}
              isLoading={keys.isLoading}
              deletingKeyId={deleteKey.isPending ? deleteKey.variables : null}
              onDelete={(key) => {
                void handleDelete(key);
              }}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}
